import { useQuery } from '@apollo/client/react'
import { Button, Icon, LoadingState, useLanguage, useTranslate } from '@athena/ui'
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useSignInSearch } from './sign-in-search.js'
import { MeDocument } from '../graphql/generated/operations.js'
import { OnboardingScreen } from '../routes/OnboardingScreen.js'

import { AppShell, type NavEntry } from './AppShell.js'
import { PageMessage } from './PageMessage.js'

/**
 * The gate in front of every tutor screen.
 *
 * `me` is the only field that tolerates an absent Viewer, which makes it the
 * natural place to ask "is anyone signed in" without an error round trip.
 * Someone signed in as a student lands here with no tutor profile, and gets
 * told so rather than an empty dashboard.
 *
 * The destinations are built inside the component rather than hoisted to a
 * module constant, because their labels are translated and a constant would
 * freeze them in whichever language happened to load first.
 */
export function TutorLayout() {
  const t = useTranslate()
  const language = useLanguage()
  const navigate = useNavigate()
  const searchQuery = useRouterState({
    select: (state) => (state.location.search as { q?: string }).q ?? '',
  })
  const { data, loading, error } = useQuery(MeDocument)
  const signInSearch = useSignInSearch()

  const nav: NavEntry[] = [
    { to: '/$language', label: t('navigation.dashboard'), icon: <Icon.LayoutDashboard /> },
    { to: '/$language/practices', label: t('navigation.practices'), icon: <Icon.BookOpen /> },
    {
      to: '/$language/practices/settings',
      label: t('practice.preferences.navLabel'),
      icon: <Icon.BookOpen />,
      parent: '/$language/practices',
    },
    { to: '/$language/students', label: t('navigation.students'), icon: <Icon.Users /> },
    { to: '/$language/plan', label: t('subscription.navLabel'), icon: <Icon.CreditCard /> },
  ]

  const viewer = data?.me
  const signedOut = !loading && !error && !viewer
  // A student who lands on a tutor URL belongs on their own side, not on an
  // error screen.
  const studentOnly = Boolean(viewer && !viewer.tutor && viewer.student)

  useEffect(() => {
    if (signedOut)
      void navigate({
        to: '/$language/sign-in',
        params: { language },
        // Back here once signed in, rather than to the dashboard.
        search: signInSearch(),
        replace: true,
      })
    else if (viewer && !viewer.onboarding.complete)
      void navigate({ to: '/$language/onboarding', params: { language }, replace: true })
    else if (studentOnly)
      void navigate({ to: '/$language/learn', params: { language }, replace: true })
    else if (viewer && !viewer.tutor && !viewer.student)
      void navigate({ to: '/$language/onboarding', params: { language }, replace: true })
  }, [signedOut, studentOnly, viewer, navigate, language, signInSearch])

  if (loading) {
    return <LoadingState>{t('common.loading')}</LoadingState>
  }

  if (error) {
    return (
      <PageMessage title={t('appError.unreachableTitle')}>
        <p className="text-body text-text-secondary">{t('appError.unreachableBody')}</p>
        <Button onClick={() => window.location.reload()}>{t('common.tryAgain')}</Button>
      </PageMessage>
    )
  }

  if (!viewer) return null

  if (!viewer.onboarding.complete) return <OnboardingScreen />

  if (!viewer.tutor) {
    // Render onboarding directly while the route transition settles. This
    // prevents the legacy invitation-only fallback from flashing or remaining
    // visible at the language root for a new profile-less account.
    return studentOnly ? null : viewer.student ? (
      <PageMessage title={t('appError.notSetUpTitle')}>
        <p className="text-body text-text-secondary">
          {t('appError.notSetUpBody', [viewer.email])}
        </p>
      </PageMessage>
    ) : (
      <OnboardingScreen />
    )
  }

  return (
    <AppShell
      viewer={{
        name: viewer.tutor.displayName,
        email: viewer.email,
        avatarUrl: viewer.avatarUrl,
      }}
      nav={nav}
      // The dashboard. Named here so the brand's link does not depend on it
      // staying the first destination in the list.
      home="/$language"
      pinnedAction={{
        to: '/$language/practices/new',
        label: t('navigation.createPractice'),
        shortLabel: t('navigation.createPracticeShort'),
        icon: <Icon.Plus />,
      }}
      search={{
        defaultValue: searchQuery,
        onSubmit: (query) => {
          void navigate({
            to: '/$language/practices',
            params: { language },
            search: query ? { q: query } : {},
          })
        },
      }}
    >
      <Outlet />
    </AppShell>
  )
}
